import { useContext } from "react";
import { useMutation, useQueryClient } from "react-query";
import { DRAFT_ACTIONS } from "../../../components/draft/draftReducer";
import DraftDispatchContext from "../../../contexts/draftDispatchContext";
import http from "../../../services/httpService";
import Util from "../../../util/util";
import useUrls from "../../useUrls";
import useDraftId from "./useDraftId";
import useDraftTeamWatchList from "./useDraftTeamWatchList";
import useDraftUpdates from "./useDraftUpdates";

export default function useDraftMutations() {
  const draftDispatch = useContext(DraftDispatchContext);
  const queryClient = useQueryClient();
  const draftId = useDraftId();
  const { draftTeamWatchUrl, draftTeamWatchData } = useDraftTeamWatchList();
  const {
    autoPick,
    chooseSpot,
    end,
    pause,
    pick,
    resetDraft,
    resume,
    startDraft,
    undoChooseSpot,
    undoPick,
  } = useDraftUpdates();
  const { apiUrl } = useUrls();

  const autoPickMutation = useMutation(
    (pickNum) => {
      const data = { pickNum };
      const url = `${apiUrl}/drafts/${draftId}/auto-pick`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with data:`, data);
      return http.patch(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log("auto pick - mutation success");
        autoPick();
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      },
      onError: (err) => {
        console.log("auto pick - mutation ERROR");
        console.error(err);
      },
    }
  );

  const pickPlayerMutation = useMutation(
    (data) => {
      data.isAuto = false;
      const url = `${apiUrl}/drafts/${draftId}/pick-player`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with data:`, data);
      return http.patch(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log("pick player - mutation success");
        pick();
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      },
      onError: (err) => {
        console.log("pick player - mutation ERROR");
        console.error(err);
      },
    }
  );

  const chooseSpotMutation = useMutation(
    (data) => {
      const url = `${apiUrl}/drafts/${draftId}/choose-spot`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with data:`, data);
      return http.patch(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log("choose spot - mutation success");
        chooseSpot();
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      },
      onError: (err) => {
        console.log("choose spot - mutation ERROR");
        console.error(err);
      },
    }
  );

  const endMutation = useMutation(
    () => {
      const url = `${apiUrl}/drafts/${draftId}/end`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with no data`);
      return http.patch(url, null, config);
    },
    {
      onSuccess: async () => {
        console.log("end draft - mutation success");
        end();
      },
      onError: (err) => {
        console.log("end draft - mutation ERROR");
        console.error(err);
      },
    }
  );

  const pauseMutation = useMutation(
    () => {
      const url = `${apiUrl}/drafts/${draftId}/pause`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with no data`);
      return http.patch(url, null, config);
    },
    {
      onSuccess: async () => {
        console.log("pause - mutation success");
        pause();
      },
      onError: (err) => {
        console.log("pause - mutation ERROR");
        console.error(err);
      },
    }
  );

  const resumeMutation = useMutation(
    () => {
      const url = `${apiUrl}/drafts/${draftId}/resume`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with no data`);
      return http.patch(url, null, config);
    },
    {
      onSuccess: async () => {
        console.log("resume - mutation success");
        resume();
      },
      onError: (err) => {
        console.log("resume - mutation ERROR");
        console.error(err);
      },
    }
  );

  const resetDraftMutation = useMutation(
    () => {
      const url = `${apiUrl}/drafts/${draftId}/reset`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with no data`);
      return http.patch(url, null, config);
    },
    {
      onSuccess: async () => {
        console.log("reset draft - mutation success");
        resetDraft();
        draftDispatch({
          type: DRAFT_ACTIONS.SET_INFO,
          payload: { propName: "hasStarted", value: false },
        });
        await queryClient.refetchQueries(`/drafts/${draftId}`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      },
      onError: (err) => {
        console.log("reset draft - mutation ERROR");
        console.error(err);
      },
    }
  );

  const startDraftMutation = useMutation(
    () => {
      const url = `${apiUrl}/drafts/${draftId}/start`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with no data`);
      return http.patch(url, null, config);
    },
    {
      onSuccess: async () => {
        console.log("start draft - mutation success");
        startDraft();
        draftDispatch({
          type: DRAFT_ACTIONS.SET_INFO,
          payload: { propName: "hasStarted", value: true },
        });
        await queryClient.refetchQueries(`/drafts/${draftId}`);
      },
      onError: (err) => {
        console.log("start draft - mutation ERROR");
        console.error(err);
      },
    }
  );

  const undoChooseSpotMutation = useMutation(
    (data) => {
      const url = `${apiUrl}/drafts/${draftId}/undo-choose-spot`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with data:`, data);
      return http.patch(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log("undo choose spot - mutation success");
        undoChooseSpot();
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      },
      onError: (err) => {
        console.log("undo choose spot - mutation ERROR");
        console.error(err);
      },
    }
  );

  const undoPickMutation = useMutation(
    (data) => {
      const url = `${apiUrl}/drafts/${draftId}/undo-pick`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with data:`, data);
      return http.patch(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log("undo pick - mutation success");
        undoPick();
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      },
      onError: (err) => {
        console.log("undo pick - mutation ERROR");
        console.error(err);
      },
    }
  );

  const updateWatchListMutation = useMutation(
    (playerIds) => {
      const url = !!draftTeamWatchData
        ? `${apiUrl}/player-lists/${draftTeamWatchData.playerList.id}`
        : null;
      const data = { players: playerIds };
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with data:`, data);
      return http.patch(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log("update watch list - mutation success");
        if (draftTeamWatchUrl) {
          console.log("invalidating queries");
          await queryClient.refetchQueries(draftTeamWatchUrl);
        }
      },
      onError: (err) => {
        console.log("update watch list - mutation ERROR");
        console.error(err);
      },
    }
  );

  return {
    autoPickMutation,
    chooseSpotMutation,
    endMutation,
    pauseMutation,
    pickPlayerMutation,
    resetDraftMutation,
    resumeMutation,
    startDraftMutation,
    undoPickMutation,
    undoChooseSpotMutation,
    updateWatchListMutation,
  };
}
